import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { toast } from "sonner";
import { 
  Phone, 
  Plus, 
  Check, 
  X, 
  TrendingUp, 
  Clock,
  Target,
  Copy
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KPICard = ({ title, value, subtitle, icon: Icon, color = "primary" }) => (
  <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center gap-2 sm:gap-3">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
          color === "primary" ? "bg-[#f4cc1e]/20" : 
          color === "success" ? "bg-green-500/20" :
          color === "danger" ? "bg-red-500/20" : "bg-[#1a3a6a]"
        }`}>
          <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${
            color === "primary" ? "text-[#f4cc1e]" : 
            color === "success" ? "text-green-400" :
            color === "danger" ? "text-red-400" : "text-white/60"
          }`} />
        </div>
        <div>
          <p className="text-xl sm:text-2xl font-bold text-white font-['Chivo']">{value}</p>
          <p className="text-[10px] sm:text-xs text-white/60">{title}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const QuickResultButton = ({ label, icon: Icon, onClick, active, color }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-1 p-2 sm:p-3 rounded-xl border transition-all text-xs sm:text-sm font-medium ${
      active 
        ? `${color} border-transparent` 
        : 'border-[#1a3a6a] bg-[#0d2a5a] hover:bg-[#1a3a6a] text-white/70'
    }`}
  >
    <Icon size={14} className="mx-auto mb-1 sm:hidden" />
    <Icon size={16} className="mx-auto mb-1 hidden sm:block" />
    <span className="hidden sm:inline">{label}</span>
    <span className="sm:hidden">{label.split(' ')[0]}</span>
  </button>
);

const CallerDashboard = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [myLogs, setMyLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  
  const [formData, setFormData] = useState({
    customer_id: "",
    date_of_call: new Date().toISOString().split("T")[0],
    inspection_datetime: "",
    ho_manager_name: "",
    phone_number: "",
    property_address: "",
    qualified_payable: "",
    campaign: "",
    additional_notes: "",
    call_result: "",
  });
  
  const [editingLogId, setEditingLogId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, campaignsRes, logsRes] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/campaigns`),
        axios.get(`${API}/call-logs`),
      ]);
      setCustomers(customersRes.data);
      setCampaigns(campaignsRes.data.filter((c) => c.status === "active"));
      setMyLogs(logsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const today = new Date().toISOString().split("T")[0];
  const todayLogs = myLogs.filter(log => log.date_of_call === today);
  const todayCalls = todayLogs.length;
  const todayQualified = todayLogs.filter(log => log.qualified_payable === "Y").length;
  const todayAppointments = todayLogs.filter(log => log.inspection_datetime).length;
  
  const thisWeekLogs = myLogs.filter(log => {
    const logDate = new Date(log.date_of_call);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return logDate >= weekAgo;
  });
  const weekCalls = thisWeekLogs.length;
  const weekQualified = thisWeekLogs.filter(log => log.qualified_payable === "Y").length;
  const conversionRate = weekCalls > 0 ? Math.round((weekQualified / weekCalls) * 100) : 0;

  // Chart data - last 7 days
  const chartData = useMemo(() => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split("T")[0];
      const dayLogs = myLogs.filter(log => log.date_of_call === dateStr);
      days.push({
        date: date.toLocaleDateString('en-US', { weekday: 'short' }),
        calls: dayLogs.length,
        qualified: dayLogs.filter(log => log.qualified_payable === "Y").length,
      });
    }
    return days;
  }, [myLogs]);

  // Filter customers by search
  const filteredCustomers = useMemo(() => {
    if (!customerSearch) return customers;
    const search = customerSearch.toLowerCase();
    return customers.filter(c => 
      (c.company_name || c.name || '').toLowerCase().includes(search)
    );
  }, [customers, customerSearch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuickResult = (result, qualified) => {
    setFormData(prev => ({
      ...prev,
      call_result: result,
      qualified_payable: qualified
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      toast.error("Please select a customer");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/call-logs`, formData);
      toast.success("Call logged successfully!");
      
      setFormData({
        customer_id: "",
        date_of_call: new Date().toISOString().split("T")[0],
        inspection_datetime: "",
        ho_manager_name: "",
        phone_number: "",
        property_address: "",
        qualified_payable: "",
        campaign: formData.campaign,
        additional_notes: "",
        call_result: "",
      });
      setCustomerSearch("");
      setEditingLogId(null);
      
      const logsRes = await axios.get(`${API}/call-logs`);
      setMyLogs(logsRes.data);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to submit call log";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!editingLogId) {
      toast.error("No call selected for editing");
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/call-logs/${editingLogId}`, formData);
      toast.success("Call saved successfully!");
      
      setFormData({
        customer_id: "",
        date_of_call: new Date().toISOString().split("T")[0],
        inspection_datetime: "",
        ho_manager_name: "",
        phone_number: "",
        property_address: "",
        qualified_payable: "",
        campaign: formData.campaign,
        additional_notes: "",
        call_result: "",
      });
      setCustomerSearch("");
      setEditingLogId(null);
      
      const logsRes = await axios.get(`${API}/call-logs`);
      setMyLogs(logsRes.data);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to save call";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const cancelEdit = () => {
    setFormData({
      customer_id: "",
      date_of_call: new Date().toISOString().split("T")[0],
      inspection_datetime: "",
      ho_manager_name: "",
      phone_number: "",
      property_address: "",
      qualified_payable: "",
      campaign: "",
      additional_notes: "",
      call_result: "",
    });
    setCustomerSearch("");
    setEditingLogId(null);
    toast.info("Edit cancelled");
  };

  const duplicateLog = (log) => {
    setFormData({
      customer_id: log.customer_id,
      date_of_call: log.date_of_call || new Date().toISOString().split("T")[0],
      inspection_datetime: log.inspection_datetime ? log.inspection_datetime.slice(0, 16) : "",
      ho_manager_name: log.ho_manager_name || "",
      phone_number: log.phone_number || "",
      property_address: log.property_address || "",
      qualified_payable: log.qualified_payable || "",
      campaign: log.campaign || "",
      additional_notes: log.additional_notes || "",
      call_result: log.call_result || "",
    });
    setEditingLogId(log.id);
    toast.info("Editing call - Save to update or Log Call to create new");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f4cc1e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-full bg-[#f4cc1e] p-4 sm:p-6 rounded-xl min-h-screen" data-testid="caller-dashboard">
      {/* Welcome & Today's Stats */}
      <div className="bg-[#091c3c] rounded-xl p-4 sm:p-6 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-between gap-3 sm:gap-4">
          <div>
            <p className="text-xs sm:text-sm text-white/60">Welcome back,</p>
            <h1 className="text-lg sm:text-xl font-bold text-white font-['Chivo']">{user?.name}</h1>
          </div>
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="text-center">
              <p className="text-xl sm:text-3xl font-bold text-[#f4cc1e] font-['Chivo']">{todayCalls}</p>
              <p className="text-[10px] sm:text-xs text-white/60">Today</p>
            </div>
            <div className="h-8 sm:h-10 w-px bg-[#1a3a6a]" />
            <div className="text-center">
              <p className="text-xl sm:text-3xl font-bold text-green-400 font-['Chivo']">{todayQualified}</p>
              <p className="text-[10px] sm:text-xs text-white/60">Qualified</p>
            </div>
            <div className="h-8 sm:h-10 w-px bg-[#1a3a6a]" />
            <div className="text-center">
              <p className="text-xl sm:text-3xl font-bold text-white font-['Chivo']">{todayAppointments}</p>
              <p className="text-[10px] sm:text-xs text-white/60">Appts</p>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <KPICard
          title="This Week"
          value={weekCalls}
          icon={Phone}
          color="secondary"
        />
        <KPICard
          title="Week Qualified"
          value={weekQualified}
          icon={Check}
          color="success"
        />
        <KPICard
          title="Conversion Rate"
          value={`${conversionRate}%`}
          icon={TrendingUp}
          color="primary"
        />
        <KPICard
          title="Total All Time"
          value={myLogs.length}
          icon={Target}
          color="secondary"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Call Entry Form */}
        <div className="lg:col-span-2">
          <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
            <CardHeader className="border-b border-[#1a3a6a] pb-4">
              <CardTitle className="font-['Chivo'] flex items-center gap-2 text-white">
                <Phone size={20} className="text-[#f4cc1e]" />
                {editingLogId ? "Edit Call" : "Log New Call"}
                {editingLogId && (
                  <span className="ml-2 px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-lg">
                    Editing
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Quick Result Buttons */}
                <div>
                  <Label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">Quick Result</Label>
                  <div className="flex gap-2">
                    <QuickResultButton
                      label="Qualified"
                      icon={Check}
                      active={formData.qualified_payable === "Y"}
                      onClick={() => handleQuickResult("appointment_set", "Y")}
                      color="bg-green-600 text-white"
                    />
                    <QuickResultButton
                      label="Not Qualified"
                      icon={X}
                      active={formData.qualified_payable === "N"}
                      onClick={() => handleQuickResult("not_interested", "N")}
                      color="bg-red-600 text-white"
                    />
                    <QuickResultButton
                      label="Callback"
                      icon={Phone}
                      active={formData.call_result === "callback"}
                      onClick={() => handleQuickResult("callback", "P")}
                      color="bg-blue-600 text-white"
                    />
                    <QuickResultButton
                      label="No Answer"
                      icon={Clock}
                      active={formData.call_result === "no_answer"}
                      onClick={() => handleQuickResult("no_answer", "P")}
                      color="bg-[#f4cc1e] text-[#091c3c]"
                    />
                  </div>
                </div>

                {/* Customer & Campaign Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white/80">Customer *</Label>
                    <Select
                      value={formData.customer_id}
                      onValueChange={(value) => handleSelectChange("customer_id", value)}
                    >
                      <SelectTrigger data-testid="customer-select" className="bg-[#0d2a5a] border-[#1a3a6a] text-white">
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                        <div className="p-2">
                          <Input
                            placeholder="Search customers..."
                            value={customerSearch}
                            onChange={(e) => setCustomerSearch(e.target.value)}
                            className="h-8 text-sm bg-[#0d2a5a] border-[#1a3a6a] text-white"
                          />
                        </div>
                        {filteredCustomers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id} className="text-white">
                            {customer.company_name || customer.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white/80">Campaign</Label>
                    <Select
                      value={formData.campaign}
                      onValueChange={(value) => handleSelectChange("campaign", value)}
                    >
                      <SelectTrigger data-testid="campaign-select" className="bg-[#0d2a5a] border-[#1a3a6a] text-white">
                        <SelectValue placeholder="Select campaign" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                        {campaigns.map((campaign) => (
                          <SelectItem key={campaign.id} value={campaign.name} className="text-white">
                            {campaign.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Contact Info Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white/80">HO/Manager Name</Label>
                    <Input
                      name="ho_manager_name"
                      value={formData.ho_manager_name}
                      onChange={handleInputChange}
                      placeholder="Contact name"
                      className="bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/40"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/80">Phone Number</Label>
                    <Input
                      name="phone_number"
                      type="tel"
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      className="bg-[#0d2a5a] border-[#1a3a6a] text-white font-mono placeholder:text-white/40"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/80">Inspection Date/Time</Label>
                    <Input
                      name="inspection_datetime"
                      type="datetime-local"
                      value={formData.inspection_datetime}
                      onChange={handleInputChange}
                      className="bg-[#0d2a5a] border-[#1a3a6a] text-white"
                    />
                  </div>
                </div>

                {/* Address */}
                <div className="space-y-2">
                  <Label className="text-white/80">Property Address</Label>
                  <Input
                    name="property_address"
                    value={formData.property_address}
                    onChange={handleInputChange}
                    placeholder="Enter property address"
                    className="bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/40"
                  />
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label className="text-white/80">Notes</Label>
                  <Textarea
                    name="additional_notes"
                    value={formData.additional_notes}
                    onChange={handleInputChange}
                    placeholder="Any additional notes..."
                    rows={2}
                    className="bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/40"
                  />
                </div>

                {/* Action Buttons */}
                {editingLogId ? (
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      onClick={handleSave}
                      disabled={submitting}
                      className="flex-1 h-12 bg-green-600 text-white hover:bg-green-700 font-bold uppercase tracking-wide rounded-xl"
                    >
                      {submitting ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Saving...
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Check size={18} />
                          Save Call
                        </div>
                      )}
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 h-12 bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-bold uppercase tracking-wide rounded-xl"
                    >
                      <div className="flex items-center gap-2">
                        <Plus size={18} />
                        Log as New
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={cancelEdit}
                      className="h-12 px-4 rounded-xl border-[#1a3a6a] text-white hover:bg-[#1a3a6a]"
                    >
                      <X size={18} />
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="submit-call-btn"
                    className="w-full h-12 bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-bold uppercase tracking-wide rounded-xl"
                  >
                    {submitting ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#091c3c]"></div>
                        Logging...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Plus size={18} />
                        Log Call
                      </div>
                    )}
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar - Stats & Recent */}
        <div className="space-y-4 sm:space-y-6">
          {/* 7-Day Chart */}
          <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
            <CardHeader className="border-b border-[#1a3a6a] pb-3">
              <CardTitle className="font-['Chivo'] text-base text-white">Last 7 Days</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1a3a6a" />
                    <XAxis dataKey="date" stroke="#9ca3af" fontSize={10} tick={{ fill: '#9ca3af' }} />
                    <YAxis stroke="#9ca3af" fontSize={10} tick={{ fill: '#9ca3af' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#091c3c',
                        border: '1px solid #1a3a6a',
                        borderRadius: '8px',
                        fontSize: '12px',
                        color: '#ffffff'
                      }}
                    />
                    <Bar dataKey="qualified" name="Qualified" fill="#22C55E" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="calls" name="Calls" stroke="#f4cc1e" strokeWidth={2} dot={{ r: 3, fill: '#f4cc1e' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Recent Calls */}
          <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
            <CardHeader className="border-b border-[#1a3a6a] pb-3">
              <CardTitle className="font-['Chivo'] text-base text-white">Recent Calls</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {myLogs.length === 0 ? (
                <div className="p-4 text-center text-white/50 text-sm">
                  No calls logged yet
                </div>
              ) : (
                <div className="divide-y divide-[#1a3a6a] max-h-[350px] overflow-auto">
                  {myLogs.slice(0, 10).map((log) => (
                    <div 
                      key={log.id} 
                      className="p-3 hover:bg-[#0d2a5a] transition-colors group cursor-pointer"
                      onClick={() => duplicateLog(log)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-white text-sm truncate">
                            {log.customer_name || "Unknown"}
                          </p>
                          <p className="text-xs text-white/50 mt-0.5">
                            {log.date_of_call} • {log.ho_manager_name || 'No contact'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {log.qualified_payable === "Y" ? (
                            <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                              <Check size={12} className="text-green-400" />
                            </div>
                          ) : log.qualified_payable === "N" ? (
                            <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center">
                              <X size={12} className="text-red-400" />
                            </div>
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-[#1a3a6a] flex items-center justify-center">
                              <Clock size={12} className="text-white/50" />
                            </div>
                          )}
                          <Copy size={14} className="text-white/40 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CallerDashboard;
